import React, { useState, useEffect } from "react";
import { Box, Popover, ClickAwayListener, Typography, IconButton, Button } from "@mui/material";
import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";

function getMonthDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const currentDate = new Date(startDate);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return days;
}

function getDefaultDateAndTime() {
  const n = new Date();
  n.setSeconds(0, 0);
  let add = 5 - (n.getMinutes() % 5);
  if (add === 0) add = 5;
  n.setMinutes(n.getMinutes() + add);
  if (n <= new Date()) {
    n.setMinutes(n.getMinutes() + 5);
  }
  return {
    day: new Date(n.getFullYear(), n.getMonth(), n.getDate()),
    time: `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`,
  };
}

/**
 * Picker for local wall-clock "YYYY-MM-DD HH:mm" (no UTC conversion) — see handleConfirm.
 */
export default function ReminderScheduleDateTimePopover({
  open,
  anchorEl,
  onClose,
  onConfirm,
}) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [selectedTime, setSelectedTime] = useState("12:00");
  const [timeView, setTimeView] = useState(false);

  useEffect(() => {
    if (!open) return;
    const { day, time } = getDefaultDateAndTime();
    setSelectedDate(day);
    setSelectedTime(time);
    setTimeView(false);
  }, [open]);

  const handlePickerClose = () => {
    onClose();
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setTimeView(true);
  };

  const handlePickerConfirm = () => {
    const [hours, minutes] = selectedTime.split(":");
    const newDateTime = new Date(selectedDate);
    newDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    const now = new Date();
    if (newDateTime <= now) {
      enqueueSnackbar(
        formatMessage({
          id: "collector.reminder_future_time_required",
          defaultMessage: "Please select a future date and time",
        }),
        { variant: "warning" }
      );
      return;
    }

    const y = selectedDate.getFullYear();
    const mo = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const da = String(selectedDate.getDate()).padStart(2, "0");
    const localStr = `${y}-${mo}-${da} ${selectedTime}`;
    onConfirm(localStr);
    handlePickerClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handlePickerClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPopover-paper": {
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(25, 118, 210, 0.15)",
          border: "1px solid rgba(25, 118, 210, 0.1)",
          overflow: "hidden",
          minWidth: "320px",
        },
      }}
    >
      <ClickAwayListener onClickAway={handlePickerClose}>
        <Box sx={{ p: 0 }}>
          {!timeView ? (
            <Box sx={{ p: 3, backgroundColor: "#fff" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <IconButton
                  onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}
                  size="small"
                  sx={{ color: "#1976d2" }}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: "600", color: "#1976d2" }}>
                  {selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </Typography>
                <IconButton
                  onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))}
                  size="small"
                  sx={{ color: "#1976d2" }}
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, mb: 2 }}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <Typography
                    key={day}
                    variant="caption"
                    sx={{
                      textAlign: "center",
                      fontWeight: "600",
                      color: "#666",
                      fontSize: "0.75rem",
                    }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
                {getMonthDays(selectedDate).map((date, index) => {
                  const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
                  const isSelected = date.toDateString() === selectedDate.toDateString();
                  const isToday = date.toDateString() === new Date().toDateString();
                  const startToday = new Date();
                  startToday.setHours(0, 0, 0, 0);
                  const isPast = date < startToday;

                  return (
                    <Box
                      key={index}
                      onClick={() => !isPast && handleDateSelect(date)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "36px",
                        borderRadius: "8px",
                        cursor: isPast || !isCurrentMonth ? "not-allowed" : "pointer",
                        backgroundColor: isSelected
                          ? "#1976d2"
                          : isToday && isCurrentMonth
                            ? "rgba(25, 118, 210, 0.1)"
                            : "transparent",
                        color: isSelected
                          ? "#fff"
                          : isPast || !isCurrentMonth
                            ? "#ccc"
                            : isToday && isCurrentMonth
                              ? "#1976d2"
                              : "#333",
                        fontWeight: isSelected || (isToday && isCurrentMonth) ? "600" : "400",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "#1565c0"
                            : isCurrentMonth && !isPast
                              ? "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                          transform: isCurrentMonth && !isPast ? "scale(1.05)" : "scale(1)",
                        },
                      }}
                    >
                      {date.getDate()}
                    </Box>
                  );
                })}
              </Box>

              <Button
                onClick={() => handleDateSelect(new Date())}
                fullWidth
                variant="outlined"
                sx={{
                  mt: 2,
                  borderColor: "#1976d2",
                  color: "#1976d2",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#1565c0",
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
                startIcon={<TodayIcon />}
              >
                {formatMessage({ id: "collector_configure.today", defaultMessage: "Today" })}
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 3, backgroundColor: "#fff", minWidth: "360px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <IconButton onClick={() => setTimeView(false)} size="small" sx={{ color: "#1976d2" }}>
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: "600", color: "#1976d2" }}>
                  {formatMessage({ id: "collector_configure.select_time", defaultMessage: "Select Time" })}
                </Typography>
                <Box sx={{ width: "40px" }} />
              </Box>

              <Box
                sx={{
                  textAlign: "center",
                  mb: 3,
                  py: 1.5,
                  background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.06) 100%)",
                  borderRadius: "12px",
                  border: "1px solid rgba(25,118,210,0.15)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "2.25rem",
                    fontWeight: 700,
                    color: "#1565c0",
                    letterSpacing: "0.12em",
                    fontFamily: '"Roboto Mono", "Courier New", monospace',
                    lineHeight: 1,
                  }}
                >
                  {selectedTime}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                  {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  display: "block",
                  mb: 1,
                }}
              >
                {formatMessage({ id: "collector_configure.hour", defaultMessage: "Hour" })}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75, mb: 2.5 }}>
                {Array.from({ length: 24 }, (_, i) => {
                  const hStr = i.toString().padStart(2, "0");
                  const isSelected = hStr === selectedTime.split(":")[0];
                  return (
                    <Box
                      key={i}
                      onClick={() => setSelectedTime(`${hStr}:${selectedTime.split(":")[1]}`)}
                      sx={{
                        textAlign: "center",
                        py: 0.8,
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        fontWeight: isSelected ? 700 : 400,
                        bgcolor: isSelected ? "#1976d2" : "rgba(25,118,210,0.05)",
                        color: isSelected ? "#fff" : "#444",
                        border: "1px solid",
                        borderColor: isSelected ? "#1565c0" : "rgba(25,118,210,0.1)",
                        transition: "all 0.15s ease",
                        userSelect: "none",
                        "&:hover": {
                          bgcolor: isSelected ? "#1565c0" : "rgba(25,118,210,0.14)",
                          borderColor: "#1976d2",
                          transform: "scale(1.08)",
                        },
                      }}
                    >
                      {hStr}
                    </Box>
                  );
                })}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  display: "block",
                  mb: 1,
                }}
              >
                {formatMessage({ id: "collector_configure.minute", defaultMessage: "Minute" })}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75, mb: 3 }}>
                {Array.from({ length: 12 }, (_, i) => {
                  const mStr = (i * 5).toString().padStart(2, "0");
                  const isSelected = mStr === selectedTime.split(":")[1];
                  return (
                    <Box
                      key={i}
                      onClick={() => setSelectedTime(`${selectedTime.split(":")[0]}:${mStr}`)}
                      sx={{
                        textAlign: "center",
                        py: 0.8,
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        fontWeight: isSelected ? 700 : 400,
                        bgcolor: isSelected ? "#1976d2" : "rgba(25,118,210,0.05)",
                        color: isSelected ? "#fff" : "#444",
                        border: "1px solid",
                        borderColor: isSelected ? "#1565c0" : "rgba(25,118,210,0.1)",
                        transition: "all 0.15s ease",
                        userSelect: "none",
                        "&:hover": {
                          bgcolor: isSelected ? "#1565c0" : "rgba(25,118,210,0.14)",
                          borderColor: "#1976d2",
                          transform: "scale(1.08)",
                        },
                      }}
                    >
                      {mStr}
                    </Box>
                  );
                })}
              </Box>

              <Button
                onClick={handlePickerConfirm}
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#1976d2",
                  borderRadius: "10px",
                  textTransform: "none",
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  "&:hover": { backgroundColor: "#1565c0" },
                }}
              >
                {formatMessage({ id: "collector_configure.confirm_time", defaultMessage: "Confirm Time" })}
              </Button>
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    </Popover>
  );
}
